import { create } from 'zustand';
import { subscriptionsAPI } from '../lib/api';
import { analyticsService } from '../lib/analyticsService';

// In a real monorepo, you'd import FEATURES from a shared package.
// For now, mirroring the keys to ensure safety.
export const FEATURES = {
  ORDER_PROCESSING: 'order_processing',
  INVENTORY_MANAGEMENT: 'inventory_management',
  CUSTOMER_MANAGEMENT: 'customer_management',
  SUPPLIER_MANAGEMENT: 'supplier_management',
  INVOICE_OCR: 'invoice_ocr',
  MULTI_LANGUAGE: 'multi_language',
  ACCOUNTING_SYNC: 'accounting_sync',
  GST_REPORTS: 'gst_reports',
  AUTO_INVOICE: 'auto_invoice',
  MULTI_LOCATION: 'multi_location',
  TEAM_COLLABORATION: 'team_collaboration',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  CUSTOM_INTEGRATIONS: 'custom_integrations',
  PRIORITY_SUPPORT: 'priority_support'
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useSubscriptionStore = create((set, get) => ({
  subscription: null, // Stores the full subscription object
  plan: null,
  features: [],
  limits: {},
  usage: {},
  billingHistory: [],
  loading: false,
  error: null,
  lastFetched: null,

  fetchSubscription: async (forceRefresh = false) => {
    const { lastFetched, loading } = get();
    
    // Cache check - bypass if forceRefresh is true
    if (!forceRefresh && lastFetched && (Date.now() - lastFetched < CACHE_DURATION)) {
      analyticsService.trackSubscriptionCheck('cache_hit');
      return;
    }

    // If already loading and not forcing refresh, skip to avoid duplicate requests
    // But allow force refresh to proceed even if loading (to handle race conditions)
    if (loading && !forceRefresh) return;

    set({ loading: true, error: null });
    try {
      const response = await subscriptionsAPI.getCurrent();
      
      const { data } = response;
      
      // Always update lastFetched timestamp, especially important for force refresh
      const now = Date.now();
      
      set({
        subscription: data,
        plan: data.planDetails,
        features: data.features || [],
        limits: data.planDetails?.limits || {},
        usage: data.usage || {},
        loading: false,
        error: null,
        lastFetched: now // Update timestamp to ensure cache is fresh
      });
      
      analyticsService.trackSubscriptionCheck(forceRefresh ? 'force_refresh_success' : 'success');
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      set({ 
        loading: false, 
        error: error.message || 'Failed to fetch subscription',
        // Don't update lastFetched on error to allow retry
      });
      analyticsService.trackSubscriptionCheck('failure');
    }
  },

  fetchBillingHistory: async () => {
    try {
      const response = await subscriptionsAPI.getBillingHistory();
      if (response.success) {
        set({ billingHistory: response.data });
      }
    } catch (error) {
      console.error('Failed to fetch billing history:', error);
    }
  },

  hasFeature: (feature) => {
    const { features, plan } = get();
    const hasAccess = features.includes(feature);
    
    if (!hasAccess) {
      analyticsService.trackFeatureRestriction(feature, plan?.id);
    }
    
    return hasAccess;
  },

  checkLimit: (metric) => {
    const { limits } = get();
    // Logic for limits checking
    return true; 
  }
}));
